/**
 * Login Page
 * Apple-inspired clean design with Google Sign-In
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/common/SEO';
import { Eye, EyeOff, AlertCircle, Ship, Users, ArrowRight } from 'lucide-react';

function LoginPage() {
  const navigate = useNavigate();
  const { login, socialLogin, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialError, setSocialError] = useState('');

  const handleChange = (e) => {
    clearError();
    setSocialError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(formData);
      const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
      navigate(dashboardPath);
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setSocialError('');
      try {
        const data = await socialLogin('google', tokenResponse.access_token, {});

        if (data.needs_completion) {
          navigate('/register');
        } else {
          const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
          navigate(dashboardPath);
        }
      } catch (err) {
        setSocialError(err.message || 'Google sign-in failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      setSocialError(errorResponse?.error_description || 'Google sign-in failed. Please try again.');
    },
    onNonOAuthError: (error) => {
      console.error('Google non-OAuth error:', error);
      if (error?.type === 'popup_closed') {
        setSocialError('Google sign-in was cancelled.');
      } else {
        setSocialError('Google sign-in could not load. Please check your browser settings and try again.');
      }
    },
  });

  const displayError = error || socialError;

  return (
    <div>
      <SEO
        title="Sign In"
        description="Sign in to AnchorPals to manage your mooring requests or find docking jobs as a helper. Access your dashboard, messages, and bookings."
        path="/login"
      />
      {/* Registration Cards */}
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-navy-900 tracking-tight mb-2">
          Get Started
        </h1>
        <p className="text-navy-500 font-light mb-6">
          Join our community of owners/captains and helpers
        </p>

        <div className="grid grid-cols-1 gap-3">
          <Link
            to="/register?type=owner"
            className="flex items-center p-4 bg-navy-900 text-white rounded-apple-lg transition-all duration-300 hover:bg-navy-800 group"
          >
            <div className="h-11 w-11 bg-white/10 rounded-apple flex items-center justify-center mr-4">
              <Ship className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-base font-medium">Register as Captain / Owner</div>
              <div className="text-navy-400 text-sm font-light">I need help mooring my yacht</div>
            </div>
            <ArrowRight className="h-5 w-5 text-navy-400 group-hover:translate-x-0.5 transition-transform duration-300" />
          </Link>
          <Link
            to="/register?type=helper"
            className="flex items-center p-4 bg-navy-100 text-navy-900 rounded-apple-lg transition-all duration-300 hover:bg-navy-200 group"
          >
            <div className="h-11 w-11 bg-navy-200 rounded-apple flex items-center justify-center mr-4">
              <Users className="h-5 w-5 text-navy-600" />
            </div>
            <div className="flex-1">
              <div className="text-base font-medium">Register as Helper</div>
              <div className="text-navy-500 text-sm font-light">I want to assist owners/captains</div>
            </div>
            <ArrowRight className="h-5 w-5 text-navy-400 group-hover:translate-x-0.5 transition-transform duration-300" />
          </Link>
        </div>
      </div>

      {/* Divider */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-navy-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-navy-400 font-light">Already have an account?</span>
        </div>
      </div>

      {/* Login Form */}
      <h2 className="text-xl font-semibold text-navy-900 mb-6 tracking-tight">
        Sign In
      </h2>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm font-light">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-normal text-navy-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input-field"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="password" className="block text-sm font-normal text-navy-700">
              Password
            </label>
            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-300">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="input-field pr-12"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600 transition-colors duration-300"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3.5 text-base"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <span className="spinner h-5 w-5 mr-2" />
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
    </div>
  );
}

export default LoginPage;
